/**
 * Calculations-spezifische API-Funktionen (GuV, Cashflow)
 * Gewinn- und Verlustrechnung, Cashflow-Berechnung
 */
import { api } from './client';
import {
  ProfitAndLoss,
  ProfitAndLossCalculateParams,
  CashflowResponse,
  CashflowCalculateParams,
  CalculationPageResponse,
  WirtschaftlichkeitSimulationRequest,
  WirtschaftlichkeitSimulationResponse
} from '../../types';

export const calculationsApi = {
  async getProfitAndLoss(projectId: string): Promise<ProfitAndLoss[]> {
    return api.get(`/projects/${projectId}/profit-and-loss`);
  },

  /**
   * Berechnet die GuV für ein Projekt basierend auf aktuellen Projektdaten
   */
  async calculateProfitAndLoss(
    projectId: string,
    params?: ProfitAndLossCalculateParams
  ): Promise<ProfitAndLoss[]> {
    const queryParams = new URLSearchParams();

    if (params?.laufzeit_jahre !== undefined) {
      queryParams.append('laufzeit_jahre', String(params.laufzeit_jahre));
    }
    if (params?.speichern !== undefined) {
      queryParams.append('speichern', String(params.speichern));
    }

    const queryString = queryParams.toString();
    const url = `/projects/${projectId}/profit-and-loss/calculate${queryString ? `?${queryString}` : ''}`;

    return api.post(url, {});
  },

  async getProfitAndLossSummary(projectId: string): Promise<Record<string, number>> {
    return api.get(`/projects/${projectId}/profit-and-loss/summary`);
  },

  async deleteProfitAndLoss(projectId: string): Promise<void> {
    return api.delete(`/projects/${projectId}/profit-and-loss`);
  },

  // Cashflow

  /**
   * Berechnet den Cashflow für ein Projekt
   * 
   * Formel: Cashflow = Ergebnis nach Steuern + Abschreibungen - Tilgung
   */
  async calculateCashflow(
    projectId: string,
    params?: CashflowCalculateParams
  ): Promise<CashflowResponse> {
    const queryParams = new URLSearchParams();

    if (params?.laufzeit_jahre !== undefined) {
      queryParams.append('laufzeit_jahre', String(params.laufzeit_jahre));
    }

    const queryString = queryParams.toString();
    const url = `/projects/${projectId}/cashflow/calculate${queryString ? `?${queryString}` : ''}`;

    return api.post(url, {});
  },

  // Combined (Kalkulationsseite)

  /**
   * Lädt gespeicherte Kalkulationsergebnisse (GuV + Cashflow + KPIs).
   * Kein Neurechnen – liest nur aus der DB.
   */
  async getCalculationPage(projectId: string): Promise<CalculationPageResponse> {
    return api.get(`/projects/${projectId}/calculation-page`);
  },

  /**
   * Berechnet GuV + Cashflow in einem Request (Combined Endpoint).
   * Persistiert die Ergebnisse (GuV + Cashflow + KPIs) in der DB.
   */
  async calculateAll(projectId: string): Promise<CalculationPageResponse> {
    return api.post(`/projects/${projectId}/calculate-all`, {});
  },

  /**
   * Rechnet die Wirtschaftlichkeit eines Projekts mit abweichendem Zuschlagswert und/oder
   * Kreditzins durch, ohne das Ergebnis zu speichern.
   *
   * Für die Projektübersicht, in der alle Projekte zum Vergleich auf denselben Wert gestellt
   * werden. Die Übersicht ruft den Endpunkt je Projekt auf, um den Fortschritt anzeigen zu
   * können; das Ergebnis lebt nur im Browser.
   */
  async simuliereWirtschaftlichkeit(
    projectId: string,
    request: WirtschaftlichkeitSimulationRequest
  ): Promise<WirtschaftlichkeitSimulationResponse> {
    return api.post(`/projects/${projectId}/wirtschaftlichkeit/simulieren`, request);
  },
};

